import { RefObject, useContext, createRef, useEffect, useId, type JSX } from 'react';
import { useAtomSelector } from 'src/hooks/useSelector';
import { Box, Dialog, IconButton, useTheme } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { zIndex } from 'src/utils/widget/zIndex';
import { modalAtom, isFirst, addId, removeId } from 'src/state/ui/modal.atom';
import { useFocusTrap } from 'src/hooks/common/useFocusTrap';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { ensurePixelValue } from 'src/components/utils/styleUtils';

export default function Modal({
    open,
    children,
    onClose,
    refocusElementOnClose,
    shadowRoot,
    large,
    elevated,
}: {
    open: boolean;
    children: JSX.Element;
    onClose: () => void;
    refocusElementOnClose?: string | RefObject<HTMLElement | null>;
    shadowRoot?: ShadowRoot;
    large?: boolean;
    // Opt-in: a neutral hairline border + soft drop shadow so the modal reads clearly against
    // the backdrop. Default keeps the legacy pale `bookingWidgetPrimaryColor` border and no
    // shadow, so every existing modal stays pixel-identical.
    elevated?: boolean;
}) {
    const mountPoint = useContext(MountPointContext);
    const popoverRef = createRef<HTMLDivElement>();
    const id = useId();
    const isIdFirst = useAtomSelector(modalAtom, isFirst, id);
    const trap = useFocusTrap(popoverRef, true, refocusElementOnClose, shadowRoot);

    const customizations = useCustomizations();
    const theme = useTheme();
    const isDark = customizations.darkMode === true;

    const close = () => {
        if (trap) trap.deactivate(50);
        onClose();
    };

    useEffect(() => {
        if (open) modalAtom.update(addId(id));
        else modalAtom.update(removeId(id));

        return () => {
            modalAtom.update(removeId(id));
        };
    }, [id, open]);

    return (
        <Dialog
            open={open}
            container={mountPoint.popover}
            onClose={close}
            tabIndex={0}
            disableEnforceFocus={true}
            ref={popoverRef}
            slotProps={
                isIdFirst
                    ? {}
                    : {
                          backdrop: {
                              sx: { backgroundColor: 'transparent' }, // Makes the backdrop invisible
                          },
                      }
            }
        >
            <Box
                position="fixed"
                zIndex={zIndex.alwaysVisible}
                border="1px solid"
                left="50%"
                top="50%"
                borderRadius={`calc(${ensurePixelValue(theme.shape.borderRadius)} * 2)`}
                overflow="hidden"
                borderColor={
                    elevated
                        ? isDark
                            ? 'rgba(255, 255, 255, 0.14)'
                            : 'rgba(0, 0, 0, 0.12)'
                        : customizations.bookingWidgetPrimaryColor
                }
                maxHeight="95vh"
                maxWidth={large ? '780px' : '450px'}
                width="100%"
                sx={{
                    transform: `translateX(-50%) translateY(-50%)`,
                    overflowY: 'auto',
                    ...(elevated && { boxShadow: '0 28px 70px -22px rgba(0, 0, 0, 0.6)' }),
                    [theme.breakpoints.down('md')]: {
                        width: `calc(100% - ${theme.spacing(2)}) !important`,
                    },
                    '& > *:not(#modal-close-button)': {
                        width: '100% !important',
                        height: '100% !important',
                        maxWidth: '100% !important',
                        margin: '0px !important',
                    },
                }}
            >
                {children}
                <IconButton
                    id="modal-close-button"
                    aria-label="Close modal"
                    onClick={close}
                    sx={{
                        position: 'absolute',
                        top: theme.spacing(1.5),
                        right: theme.spacing(1.5),
                        width: 'auto !important',
                        height: 'auto !important',
                        maxWidth: 'unset !important',
                        '& .MuiSvgIcon-root': {
                            // Elevated modals sit on the paper background (no coloured header), so the
                            // legacy bookingWidgetColorContrast X (often white) would be invisible — use a
                            // neutral text colour that adapts to light/dark. Other modals keep the legacy X.
                            color: elevated
                                ? theme.palette.text.secondary
                                : customizations.bookingWidgetColorContrast,
                        },
                    }}
                >
                    <CloseRounded />
                </IconButton>
            </Box>
        </Dialog>
    );
}
